import React, { useCallback, useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { GrPrevious, GrNext } from "react-icons/gr";
import { TbDevices, TbEyeSearch } from "react-icons/tb";

import api from "../api/axios";
import {
  SessionSummary,
  describeRevocation,
  formatSessionTime,
} from "../components/DeviceSession";
import "../assets/styles/page.css";
import "../assets/styles/directory-tables.css";
import "../assets/styles/device-sessions.css";

const PAGE_NAME = "Login Security";
const BASE = "/admin/login-security";

const headersFor = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const errorMessage = (error, fallback) =>
  error.response?.data?.message || fallback;

const LoginSecurity = () => {
  const [settings, setSettings] = useState(null);
  const [limitsDraft, setLimitsDraft] = useState({ customer: "", admin: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [role, setRole] = useState("");
  const [page, setPage] = useState(1);
  const [overrideDrafts, setOverrideDrafts] = useState({});

  const [selectedUser, setSelectedUser] = useState(null);
  const [userSessions, setUserSessions] = useState({ active: [], history: [] });
  const [loadingSessions, setLoadingSessions] = useState(false);

  const canEdit = Boolean(settings?.can_edit);
  const canDelete = Boolean(settings?.can_delete);

  /* ---------- Settings ---------- */

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get(`${BASE}/settings`, { headers: headersFor("view") });
      const data = res.data?.data;
      setSettings(data);
      setLimitsDraft({
        customer: String(data.customer_max_devices),
        admin: String(data.admin_max_devices),
      });
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        return;
      }
      toast.error(errorMessage(error, "Unable to load login security settings."));
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      const res = await api.put(
        `${BASE}/settings`,
        {
          customer_max_devices: Number(limitsDraft.customer),
          admin_max_devices: Number(limitsDraft.admin),
        },
        { headers: headersFor("edit") }
      );
      setSettings(res.data?.data);
      toast.success(res.data?.message || "Device limits saved.");
      loadUsers();
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(errorMessage(error, "Unable to save device limits."));
      }
    } finally {
      setSavingSettings(false);
    }
  };

  /* ---------- Users ---------- */

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, role]);

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const res = await api.get(`${BASE}/users`, {
        headers: headersFor("view"),
        params: {
          page,
          limit: 10,
          search: debouncedSearch || undefined,
          role: role || undefined,
        },
      });
      setUsers(res.data?.data || []);
      setPagination(res.data?.pagination || { page: 1, totalPages: 1, total: 0 });
      setOverrideDrafts({});
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        return;
      }
      toast.error(errorMessage(error, "Unable to load users."));
    } finally {
      setLoadingUsers(false);
    }
  }, [page, debouncedSearch, role]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const saveOverride = async (user) => {
    const draft = overrideDrafts[user.user_id];
    const value = draft === "" ? null : Number(draft);

    try {
      const res = await api.put(
        `${BASE}/users/${user.user_id}/limit`,
        { max_devices: value },
        { headers: headersFor("edit") }
      );
      toast.success(res.data?.message || "Device limit updated.");
      loadUsers();
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(errorMessage(error, "Unable to update the device limit."));
      }
    }
  };

  /* ---------- Sessions for one user ---------- */

  const loadUserSessions = useCallback(async (user) => {
    try {
      setLoadingSessions(true);
      const res = await api.get(`${BASE}/users/${user.user_id}/sessions`, {
        headers: headersFor("view"),
        params: { history: 1 },
      });
      setUserSessions({
        active: res.data?.data?.active || [],
        history: res.data?.data?.history || [],
      });
    } catch (error) {
      toast.error(errorMessage(error, "Unable to load this user's devices."));
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const openUser = (user) => {
    setSelectedUser(user);
    setUserSessions({ active: [], history: [] });
    loadUserSessions(user);
  };

  const refreshSelected = async () => {
    if (selectedUser) await loadUserSessions(selectedUser);
    loadUsers();
  };

  const revokeSession = (session) =>
    confirmDialog({
      header: "Sign out this device?",
      message: `${selectedUser.name} will be signed out on ${session.device_label}.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sign out",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const res = await api.delete(`${BASE}/sessions/${session.session_uid}`, {
            headers: headersFor("delete"),
          });
          toast.success(res.data?.message || "Device signed out.");
          refreshSelected();
        } catch (error) {
          if (error.response?.status !== 403) {
            toast.error(errorMessage(error, "Unable to sign out that device."));
          }
        }
      },
    });

  const revokeAll = () =>
    confirmDialog({
      header: "Sign out of every device?",
      message: `${selectedUser.name} will be signed out everywhere and must sign in again.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sign out everywhere",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const res = await api.post(
            `${BASE}/users/${selectedUser.user_id}/revoke-all`,
            null,
            { headers: headersFor("edit") }
          );
          toast.success(res.data?.message || "Signed out everywhere.");
          refreshSelected();
        } catch (error) {
          if (error.response?.status !== 403) {
            toast.error(errorMessage(error, "Unable to sign this user out."));
          }
        }
      },
    });

  if (accessDenied) {
    return (
      <section className="admin-page">
        <div className="page-card maintenance-access-denied">
          <i className="pi pi-lock" aria-hidden="true" />
          <h2>Login Security permission required</h2>
          <p>Your role needs the Login Security view permission to access this page.</p>
        </div>
      </section>
    );
  }

  const policy = settings?.session_policy;

  return (
    <section className="admin-page premium-users-page">
      <ConfirmDialog />

      <div className="page-header users-hero-header">
        <div>
          <p>System controls</p>
          <h1>Login Security</h1>
          <span>Control how many devices each account can be signed in on at once.</span>
        </div>
      </div>

      <div className="page-card">
        <p className="settings-section-label">Default device limits</p>
        <h2>Devices per account</h2>
        <p className="login-security-policy">
          When an account reaches its limit, the next sign-in asks the user to sign
          out one of their devices first. Changes apply from each account's next
          sign-in; devices already signed in stay signed in.
        </p>

        <div className="login-security-settings">
          <label className="login-security-field">
            <strong>Customers</strong>
            <input
              type="number"
              min={settings?.min_devices || 1}
              max={settings?.max_devices || 20}
              value={limitsDraft.customer}
              disabled={!canEdit}
              onChange={(event) =>
                setLimitsDraft((draft) => ({ ...draft, customer: event.target.value }))
              }
            />
            <span>devices at a time</span>
          </label>

          <label className="login-security-field">
            <strong>Admins</strong>
            <input
              type="number"
              min={settings?.min_devices || 1}
              max={settings?.max_devices || 20}
              value={limitsDraft.admin}
              disabled={!canEdit}
              onChange={(event) =>
                setLimitsDraft((draft) => ({ ...draft, admin: event.target.value }))
              }
            />
            <span>devices at a time</span>
          </label>
        </div>

        {policy && (
          <p className="login-security-policy">
            Customer sessions last up to {Math.round(policy.customer.absolute_hours / 24)} days
            and end after {Math.round(policy.customer.idle_minutes / 1440)} days without use.
            Admin sessions last up to {policy.admin.absolute_hours} hours and end after{" "}
            {policy.admin.idle_minutes} minutes without use.
          </p>
        )}

        <div className="login-security-actions">
          <span className="login-security-policy">
            {settings?.updated_at
              ? `Last saved ${formatSessionTime(settings.updated_at)}`
              : "Using the default limits."}
          </span>

          {canEdit && (
            <button
              type="button"
              className="primary-action"
              onClick={saveSettings}
              disabled={savingSettings}
            >
              <i
                className={`pi ${savingSettings ? "pi-spin pi-spinner" : "pi-save"}`}
                aria-hidden="true"
              />
              {savingSettings ? "Saving..." : "Save limits"}
            </button>
          )}
        </div>
      </div>

      <div className="page-card premium-user-card">
        <div className="premium-user-toolbar">
          <div>
            <h2>Accounts</h2>
            <p>
              {loadingUsers
                ? "Loading accounts..."
                : `${pagination.total} account(s) found.`}
            </p>
          </div>

          <div className="premium-user-filters">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or email..."
              aria-label="Search accounts"
            />

            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
              aria-label="Filter by role"
            >
              <option value="">All roles</option>
              <option value="customer">Customers</option>
              <option value="admin">Admins</option>
            </select>
          </div>
        </div>

        <div className={`directory-table-scroll ${loadingUsers ? "is-loading" : ""}`}>
          <table className="directory-data-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Role</th>
                <th>Devices in use</th>
                <th>Device limit</th>
                <th>Last active</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loadingUsers && users.length === 0 && (
                <tr>
                  <td className="directory-table-empty" colSpan="6">
                    <TbDevices />
                    <strong>No accounts found</strong>
                    <span>Try changing your search or role filter.</span>
                  </td>
                </tr>
              )}

              {users.map((user) => {
                const draft = overrideDrafts[user.user_id];
                const draftValue =
                  draft !== undefined
                    ? draft
                    : user.max_devices_override === null
                      ? ""
                      : String(user.max_devices_override);
                const changed =
                  draft !== undefined &&
                  draft !==
                    (user.max_devices_override === null
                      ? ""
                      : String(user.max_devices_override));

                return (
                  <tr key={user.user_id}>
                    <td className="directory-person-cell">
                      <div className="directory-person">
                        {user.image_url ? (
                          <img src={user.image_url} alt="" />
                        ) : (
                          <span className="directory-avatar">
                            {user.name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        )}
                        <div>
                          <strong>{user.name}</strong>
                          <span>{user.email}</span>
                          <small>
                            #{user.user_id} · {user.status}
                          </small>
                        </div>
                      </div>
                    </td>

                    <td style={{ textTransform: "capitalize" }}>{user.role}</td>

                    <td>
                      <span
                        className={`login-security-usage ${
                          user.active_sessions >= user.effective_limit ? "at-limit" : ""
                        }`}
                      >
                        {user.active_sessions} / {user.effective_limit}
                      </span>
                    </td>

                    <td>
                      {canEdit ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <input
                            type="number"
                            className="login-security-limit-input"
                            min={settings?.min_devices || 1}
                            max={settings?.max_devices || 20}
                            placeholder={`Default (${user.role_default_limit})`}
                            value={draftValue}
                            aria-label={`Device limit for ${user.name}`}
                            onChange={(event) =>
                              setOverrideDrafts((drafts) => ({
                                ...drafts,
                                [user.user_id]: event.target.value,
                              }))
                            }
                          />
                          {changed && (
                            <button
                              type="button"
                              className="primary-action"
                              onClick={() => saveOverride(user)}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      ) : user.max_devices_override === null ? (
                        `Default (${user.role_default_limit})`
                      ) : (
                        user.max_devices_override
                      )}
                    </td>

                    <td className="directory-date-cell">
                      <strong>
                        {user.last_seen_at ? formatSessionTime(user.last_seen_at) : "Not signed in"}
                      </strong>
                    </td>

                    <td className="directory-actions-cell">
                      <div className="directory-row-actions">
                        <button
                          type="button"
                          title="View devices"
                          aria-label={`View ${user.name}'s devices`}
                          onClick={() => openUser(user)}
                        >
                          <TbEyeSearch />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button
            type="button"
            className="directory-pagination-direction"
            disabled={pagination.page <= 1 || loadingUsers}
            onClick={() => setPage(pagination.page - 1)}
          >
            <GrPrevious /> Previous
          </button>

          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            type="button"
            className="directory-pagination-direction"
            disabled={pagination.page >= pagination.totalPages || loadingUsers}
            onClick={() => setPage(pagination.page + 1)}
          >
            Next <GrNext />
          </button>
        </div>
      </div>

      <Sidebar
        visible={Boolean(selectedUser)}
        position="right"
        onHide={() => setSelectedUser(null)}
        className="premium-user-sidebar"
      >
        {selectedUser && (
          <div className="user-view-shell">
            <div className="user-view-title">
              <span>Signed-in devices</span>
              <h2>{selectedUser.name}</h2>
              <p>{selectedUser.email}</p>
            </div>

            <h3 className="login-security-section-title">
              Active ({userSessions.active.length} of {selectedUser.effective_limit})
            </h3>

            {loadingSessions ? (
              <div className="login-security-empty">Loading devices...</div>
            ) : userSessions.active.length === 0 ? (
              <div className="login-security-empty">Not signed in on any device.</div>
            ) : (
              <div className="login-security-session-list">
                {userSessions.active.map((session) => (
                  <div className="login-security-session" key={session.session_uid}>
                    <div>
                      <SessionSummary session={session} />
                      <div className="login-security-session-meta">
                        {session.client_app === "admin" ? "Admin console" : "Storefront"}
                        {session.ip_address ? ` · signed in from ${session.ip_address}` : ""}
                        {` · expires ${formatSessionTime(session.expires_at)}`}
                      </div>
                    </div>

                    {canDelete && (
                      <button
                        type="button"
                        className="danger-action"
                        onClick={() => revokeSession(session)}
                      >
                        Sign out
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canEdit && userSessions.active.length > 0 && (
              <div className="user-view-actions">
                <button type="button" className="danger-action" onClick={revokeAll}>
                  Sign out everywhere
                </button>
              </div>
            )}

            <h3 className="login-security-section-title">Recent history</h3>

            {userSessions.history.length === 0 ? (
              <div className="login-security-empty">No past sessions yet.</div>
            ) : (
              <div className="login-security-session-list">
                {userSessions.history.map((session) => (
                  <div className="login-security-session revoked" key={session.session_uid}>
                    <div>
                      <SessionSummary session={session} />
                      <div className="login-security-session-meta">
                        {describeRevocation(session)} · {formatSessionTime(session.revoked_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default LoginSecurity;
